/**
 * useUpdateUserRole — wraps the update-user-role Edge Function.
 *
 * The Edge Function returns { user_id, previous_role, new_role } on
 * success. Failures arrive as structured codes; we map them to
 * AppError.userMessage for inline display on the Team page.
 *
 * On success we invalidate the team + cleaner-picker caches so the
 * Team page re-groups the row under the new role section immediately.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useSupabase }                                          from './useSupabase'
import { AppError }                                             from '@/lib/errors/AppError'

export type AssignableRole = 'cleaner' | 'supervisor' | 'contractor_admin'

export interface UpdateRoleVars {
  userId:  string
  newRole: AssignableRole
}

export interface UpdateRoleResult {
  user_id:       string
  previous_role: string
  new_role:      string
}

export function useUpdateUserRole(): UseMutationResult<UpdateRoleResult, AppError, UpdateRoleVars> {
  const supabase    = useSupabase()
  const queryClient = useQueryClient()

  return useMutation<UpdateRoleResult, AppError, UpdateRoleVars>({
    mutationFn: async (vars): Promise<UpdateRoleResult> => {
      const { data, error } = await supabase.functions.invoke<UpdateRoleResult>('update-user-role', {
        body: {
          user_id:  vars.userId,
          new_role: vars.newRole,
        },
      })
      if (error) {
        const ctx = error as unknown as { context?: Response }
        let code   = 'ROLE_UPDATE_FAILED'
        let detail = error.message
        try {
          const parsed = await ctx.context?.clone().json() as { error?: string; detail?: string }
          if (parsed?.error)  code   = parsed.error
          if (parsed?.detail) detail = parsed.detail
        } catch { /* response was not JSON */ }
        throw new AppError({
          code,
          message:     detail,
          userMessage: userMessageFor(code, detail),
          status:      500,
        })
      }
      if (!data) {
        throw new AppError({
          code: 'EMPTY_RESPONSE', message: 'No data', userMessage: 'The server returned no result.', status: 500,
        })
      }
      return data
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['org-team']     })
      void queryClient.invalidateQueries({ queryKey: ['org-cleaners'] })
    },
  })
}

function userMessageFor(code: string, detail: string): string {
  switch (code) {
    case 'FORBIDDEN_ROLE':            return 'Your role is not allowed to change other users.'
    case 'FORBIDDEN_ROLE_ESCALATION': return 'Only the platform owner can assign contractor admin.'
    case 'FORBIDDEN_SELF_MOD':        return "You can't change your own role."
    case 'CROSS_ORG_FORBIDDEN':       return "That user isn't in your organization."
    case 'USER_NOT_FOUND':            return 'That user could not be found.'
    case 'INVALID_USER_ID':           return 'The selected user ID was not valid.'
    case 'INVALID_ROLE':              return 'Pick a valid role.'
    case 'NO_OP_ROLE':                return 'That user already has the role you picked.'
    case 'RATE_LIMITED':              return 'Too many role changes in a row. Pause for a moment.'
    case 'AUDIT_FAILED':              return 'Role changed but the audit log write failed — surface this to ops.'
    default:                          return detail || 'Could not change the role.'
  }
}
